import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

type ThemeType = 'light' | 'dark';

interface ThemeState {
  theme: ThemeType;
  fontSize: 'small' | 'medium' | 'large';
}

interface ThemeContextType extends ThemeState {
  toggleTheme: () => void;
  setTheme: (theme: ThemeType) => void;
  setFontSize: (fontSize: 'small' | 'medium' | 'large') => void;
}

type ThemeAction =
  | { type: 'SET_THEME'; payload: ThemeType }
  | { type: 'SET_FONT_SIZE'; payload: 'small' | 'medium' | 'large' }
  | { type: 'TOGGLE_THEME' };

const getInitialTheme = (): ThemeType => {
  // Check localStorage first
  const savedTheme = localStorage.getItem('theme') as ThemeType;
  if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
    return savedTheme;
  }

  // Check system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
};

const getInitialFontSize = (): 'small' | 'medium' | 'large' => {
  const savedFontSize = localStorage.getItem('fontSize') as 'small' | 'medium' | 'large';
  if (savedFontSize && ['small', 'medium', 'large'].includes(savedFontSize)) {
    return savedFontSize;
  }
  return 'medium';
};

const initialState: ThemeState = {
  theme: getInitialTheme(),
  fontSize: getInitialFontSize(),
};

const themeReducer = (state: ThemeState, action: ThemeAction): ThemeState => {
  switch (action.type) {
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    
    case 'SET_FONT_SIZE':
      return { ...state, fontSize: action.payload };
    
    case 'TOGGLE_THEME':
      return { ...state, theme: state.theme === 'light' ? 'dark' : 'light' };
    
    default:
      return state;
  }
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', state.theme);
    localStorage.setItem('theme', state.theme);
  }, [state.theme]);

  // Apply font size to document
  useEffect(() => {
    const fontSizes = {
      small: '14px',
      medium: '16px',
      large: '18px',
    };
    document.documentElement.style.fontSize = fontSizes[state.fontSize];
    localStorage.setItem('fontSize', state.fontSize);
  }, [state.fontSize]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a theme
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        dispatch({ type: 'SET_THEME', payload: e.matches ? 'dark' : 'light' });
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    dispatch({ type: 'TOGGLE_THEME' });
  };

  const setTheme = (theme: ThemeType) => {
    dispatch({ type: 'SET_THEME', payload: theme });
  };

  const setFontSize = (fontSize: 'small' | 'medium' | 'large') => {
    dispatch({ type: 'SET_FONT_SIZE', payload: fontSize });
  };

  const value: ThemeContextType = {
    ...state,
    toggleTheme,
    setTheme,
    setFontSize,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
